import React, { useState } from 'react';
import { BaseModal } from '@/components/shared';
import { resolverSolicitacaoRelacionada } from './solicitacaoLink.utils';

interface CancelarSolicitacaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** ID da própria Solicitação sendo cancelada — usado só pra impedir vincular ela a si
   * mesma no campo "Solicitação relacionada". */
  idAtual: string;
  /** `solicitacaoRelacionadaId` já vem validado (existe, não é a própria Solicitação) —
   * ausente/null quando o campo foi deixado em branco. */
  onConfirmar: (motivo: string, solicitacaoRelacionadaId: string | null) => void;
  confirmando?: boolean;
}

/**
 * Confirmação de cancelamento — exige o motivo (fica registrado na Timeline, ver
 * onSolicitacaoWrite). Substitui o antigo `confirm()` do navegador, que não deixava
 * nenhum histórico do porquê a Solicitação foi cancelada.
 *
 * Ganhou também o campo opcional "Solicitação relacionada" — pensado pro caso comum de
 * cancelar uma Solicitação porque já existe outra em andamento pro mesmo assunto; o
 * vínculo (Solicitacao.solicitacaoRelacionadaId) é gravado como uma escrita independente
 * do cancelamento em si (decisão de produto: não é obrigatório, e nada impede vincular só
 * fora daqui no futuro), só entra nesse mesmo fluxo por conveniência.
 */
export default function CancelarSolicitacaoModal({
  isOpen,
  onClose,
  idAtual,
  onConfirmar,
  confirmando,
}: CancelarSolicitacaoModalProps) {
  const [motivo, setMotivo] = useState('');
  const [relacionadaInput, setRelacionadaInput] = useState('');
  const [erroRelacionada, setErroRelacionada] = useState<string | null>(null);
  const [verificando, setVerificando] = useState(false);

  const handleClose = () => {
    setMotivo('');
    setRelacionadaInput('');
    setErroRelacionada(null);
    onClose();
  };

  const handleConfirmar = async () => {
    if (!motivo.trim()) return;

    if (!relacionadaInput.trim()) {
      onConfirmar(motivo.trim(), null);
      return;
    }

    setVerificando(true);
    let resultado: Awaited<ReturnType<typeof resolverSolicitacaoRelacionada>>;
    try {
      resultado = await resolverSolicitacaoRelacionada(relacionadaInput, idAtual);
    } finally {
      setVerificando(false);
    }

    if ('erro' in resultado) {
      setErroRelacionada(resultado.erro);
      return;
    }
    setErroRelacionada(null);
    onConfirmar(motivo.trim(), resultado.id);
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Cancelar Solicitação"
      maxWidth="max-w-md"
      actions={
        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={handleClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!motivo.trim() || confirmando || verificando}
            onClick={handleConfirmar}
          >
            {verificando ? 'Verificando...' : confirmando ? 'Cancelando...' : 'Confirmar Cancelamento'}
          </button>
        </div>
      }
    >
      <div className="space-y-3 py-4">
        <p className="text-sm text-gray-600">
          Informe o motivo do cancelamento — fica registrado no histórico desta Solicitação.
        </p>
        <textarea
          className="input-primary w-full"
          rows={4}
          value={motivo}
          onChange={(event) => setMotivo(event.target.value)}
          placeholder="Motivo do cancelamento *"
          autoFocus
        />

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Solicitação relacionada (opcional)
          </label>
          <input
            type="text"
            className="input-primary mt-1 w-full"
            placeholder="Cole o link (ou ID) da outra Solicitação, se for o caso"
            value={relacionadaInput}
            onChange={(event) => {
              setRelacionadaInput(event.target.value);
              if (erroRelacionada) setErroRelacionada(null);
            }}
          />
          {erroRelacionada ? (
            <p className="mt-1 text-xs text-red-600">{erroRelacionada}</p>
          ) : (
            <p className="mt-1 text-xs text-gray-500">
              Útil quando esta Solicitação está sendo cancelada por já existir outra em andamento pro mesmo assunto.
            </p>
          )}
        </div>
      </div>
    </BaseModal>
  );
}
